import { useState } from 'react';
import {
  BUSINESS_PRICING,
  FAMILY_PRICING,
  PLUS_PRICING,
} from '@/features/billing/lib/planPricing';
import {
  BUSINESS_INCLUDED_SEATS,
  BUSINESS_PRICES,
  BUSINESS_SEAT_PACK_SIZE,
} from '@/features/billing/lib/seatPacks';
import type {
  OrgPricingTier,
  PricingTierName,
  SubscriptionType,
} from '@/features/billing/types';
import {
  SegmentedControl,
  type SegmentedControlOption,
} from '@/pages/Schedule/components';
import {
  Box,
  Container,
  Heading,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  SimpleGrid,
  VStack,
} from '@/ui';
import { OrgPricingCard } from '../components/OrgPricingCard';
import { OrgPricingCta } from '../components/OrgPricingCta';
import {
  filterPricingTiers,
  getComparePlansHeading,
  getModalMinWidth,
} from '../tierColumns';

const billingIntervalOptions: SegmentedControlOption<SubscriptionType>[] = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Annual', value: 'yearly' },
];

// The compare-plans table: 4 tiers with Family between Plus and Organizations.
// Copy is hardcoded here on purpose so it's quick to iterate on. Plus, Family,
// and Organizations pricing follows the Monthly/Annual switcher; Free doesn't.
const getOrgPricingTiers = (interval: SubscriptionType): OrgPricingTier[] => [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    priceSubLabel: 'per user',
    description: 'For individuals',
    features: [
      { text: 'Unlimited calendar events' },
      { text: 'Create printable and interactive calendars' },
      { text: 'Up to 2 layouts per calendar' },
    ],
    buttonText: 'Start free',
    buttonVariant: 'outline',
    action: 'register',
  },
  {
    name: 'Plus',
    price: PLUS_PRICING[interval].price,
    period: PLUS_PRICING[interval].period,
    priceSubLabel: '1 account',
    description: 'For power users',
    features: [
      { text: 'Unlimited Magic: text, photos and PDFs' },
      { text: 'Unlimited co-editors' },
      { text: 'Unlimited sources/feeds synced' },
    ],
    buttonText: 'Go Plus',
    buttonVariant: 'solid',
    isPopular: true,
    action: 'checkout',
    packageId: PLUS_PRICING[interval].packageId,
  },
  {
    name: 'Family',
    price: FAMILY_PRICING[interval].price,
    period: FAMILY_PRICING[interval].period,
    priceSubLabel: '6 accounts included',
    description: 'For families',
    features: [
      { text: '6 accounts included' },
      {
        text: 'Text reminders to the people in your Family Plan (US numbers only)',
      },
      { text: 'Shared family calendars' },
    ],
    buttonText: 'Get Started',
    buttonVariant: 'solid',
    action: 'checkout',
    packageId: FAMILY_PRICING[interval].packageId,
  },
  {
    name: 'Organizations',
    price: BUSINESS_PRICING[interval].price,
    period: BUSINESS_PRICING[interval].period,
    priceSubLabel: `${BUSINESS_INCLUDED_SEATS} accounts to start`,
    description: 'For organizations',
    features: [
      {
        text: `${BUSINESS_INCLUDED_SEATS} accounts to start, add ${BUSINESS_SEAT_PACK_SIZE} more for $${BUSINESS_PRICES[interval].pack}/${interval === 'monthly' ? 'mo' : 'yr'} anytime`,
      },
      { text: 'Brand kit and templates' },
      { text: 'Branded URL calendar links' },
    ],
    buttonText: 'Get Started',
    buttonVariant: 'solid',
    action: 'checkout',
    packageId: BUSINESS_PRICING[interval].packageId,
    seatPacksSelectable: true,
  },
];

type Props = {
  /** Contextual heading naming the gated capability, shown above the columns. */
  heading?: string;
  /** Columns to show. Omit for the full comparison table. */
  tiers?: PricingTierName[];
  onClose: () => void;
};

export const ComparePlansOrgVariation = ({
  heading,
  tiers,
  onClose: _onClose,
}: Props) => {
  const [billingInterval, setBillingInterval] =
    useState<SubscriptionType>('yearly');

  const visibleTiers = filterPricingTiers(
    getOrgPricingTiers(billingInterval),
    tiers
  );
  const columns = visibleTiers.length;
  // A lone card is a pitch, not a comparison: the contextual heading becomes the
  // modal title, the card sheds its frame and badge, and the CTA is pinned to the
  // modal foot so it stays visible without scrolling.
  const loneTier = columns === 1 ? visibleTiers[0] : undefined;

  const billingIntervalControl = (
    <SegmentedControl
      aria-label="Billing interval"
      bg="gray.100"
      options={billingIntervalOptions}
      value={billingInterval}
      onChange={setBillingInterval}
    />
  );

  return (
    <ModalContent
      // Full-bleed on mobile, sized to the dialog container (100dvh, exactly the
      // visible viewport) rather than --app-height or vh units, both of which can
      // exceed the visible height and leave the modal clipped under browser
      // chrome (vh is the LARGE viewport on phones, including landscape ones wide
      // enough for md). Top-pinned on base so the dialog's bottom edge always
      // coincides with the visible viewport bottom.
      alignSelf={{ base: 'flex-start', md: 'center' }}
      display="flex"
      flexDirection="column"
      maxH={{ base: '100%', md: '90%' }}
      minH={{ base: '100%', md: 'auto' }}
      minW={getModalMinWidth(columns)}
      overflow="hidden"
    >
      {/* pr clears the close button; contextual headings can run long enough to
          collide with it at phone widths. */}
      <ModalHeader pr={12}>
        {loneTier && heading ? heading : getComparePlansHeading(visibleTiers)}
      </ModalHeader>
      <ModalCloseButton />
      {/* minH keeps this region shrinkable so it scrolls instead of overflowing
          the capped dialog; Safari won't shrink a flex child below its content
          height without it. */}
      <Box flex="1" minH={0} overflowY="auto" p={4}>
        <Container maxW="7xl" position="relative">
          <VStack spacing={{ base: 3, md: 6 }} textAlign="center">
            {loneTier ? (
              billingIntervalControl
            ) : (
              <VStack maxW="xl" spacing={{ base: 2, md: 4 }}>
                <Heading
                  fontWeight="bold"
                  lineHeight="shorter"
                  size={{ base: 'md', md: 'lg' }}
                >
                  {heading ?? 'Effortless planning starts here.'}
                </Heading>
                {billingIntervalControl}
              </VStack>
            )}

            <SimpleGrid
              columns={{ base: 1, md: Math.min(columns, 2), lg: columns }}
              spacing={4}
              w="full"
            >
              {visibleTiers.map((tier, index) => (
                <OrgPricingCard
                  frameless={!!loneTier}
                  hideCta={!!loneTier}
                  key={index}
                  subscriptionType={billingInterval}
                  tier={loneTier ? { ...tier, isPopular: false } : tier}
                />
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>
      {loneTier && (
        <Box borderTopColor="gray.100" borderTopWidth={1} px={4} py={3}>
          <OrgPricingCta subscriptionType={billingInterval} tier={loneTier} />
        </Box>
      )}
    </ModalContent>
  );
};
